(() => {
  if (window.ScaleShowroomCart) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const timedFetch = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try { return await fetch(url, {...options, signal:controller.signal}); }
    catch (error) {
      if (controller.signal.aborted) throw new Error('Your cart update could not be confirmed. Review your cart before trying again.');
      throw error;
    } finally { clearTimeout(timer); }
  };
  const previewPath = () => window.location.pathname + window.location.search;
  const syncRecommendations = cart => {
    const ids = new Set((cart.items || []).map(item => String(item.product_id)));
    document.querySelectorAll('[data-ss-product]').forEach(card => { card.hidden = ids.has(card.dataset.ssProduct); });
    document.querySelectorAll('.ss-display-addons').forEach(panel => {
      const cards = [...panel.querySelectorAll('[data-ss-product]')];
      panel.hidden = cards.length > 0 && cards.every(card => card.hidden);
    });
  };
  const service = {
    busy: false,
    async request(endpoint, payload, {openDrawer = false, trigger = null, focusIndex = null} = {}) {
      if (this.busy) throw new Error('Please wait while your cart updates.');
      this.busy = true;
      const controls = [...document.querySelectorAll('[data-ss-cart-control], [data-ss-cart-lounge] [data-quantity], [data-ss-cart-lounge] button[type="submit"], cart-drawer input[name="updates[]"], cart-drawer .quantity__button, cart-drawer cart-remove-button button, cart-drawer .cart__checkout-button')];
      const disabled = controls.map(el => [el, el.disabled]);
      controls.forEach(el => { el.disabled = true; });
      const lounge = document.querySelector('[data-ss-cart-lounge]');
      const note = lounge?.querySelector('[data-note]')?.value;
      const noteOpen = lounge?.querySelector('details')?.open;
      const drawerNote = document.querySelector('#CartDrawer-Note')?.value;
      const ids = [...new Set(['cart-drawer', 'cart-icon-bubble', lounge?.dataset.sectionId].filter(Boolean))];
      try {
        let body, headers = {Accept: 'application/json'};
        if (payload instanceof FormData) {
          payload.set('sections', ids.join(','));
          payload.set('sections_url', previewPath());
          body = payload;
        } else {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify({...payload, sections: ids, sections_url: previewPath()});
        }
        const response = await timedFetch(root() + endpoint, {method: 'POST', headers, body});
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Your cart could not update. Please try again.');
        let cart = data;
        if (!Array.isArray(data.items) || endpoint === 'cart/add.js') {
          const cartResponse = await timedFetch(root() + 'cart.js', {headers: {Accept: 'application/json'}});
          if (!cartResponse.ok) throw new Error('Added to cart. Open your cart to review the latest items.');
          cart = await cartResponse.json();
        }
        let sections = data.sections || {};
        if (ids.some(id => !sections[id])) {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', ids.join(','));
          try {
            const rendered = await timedFetch(url, {headers: {Accept: 'application/json'}});
            if (rendered.ok) sections = {...sections, ...await rendered.json()};
          } catch (_) { /* A confirmed mutation must never be repeated. */ }
        }
        const parse = html => new DOMParser().parseFromString(html, 'text/html');
        if (lounge) {
          const fresh = sections[lounge.dataset.sectionId] && parse(sections[lounge.dataset.sectionId]).querySelector('[data-ss-cart-lounge]');
          if (!fresh) {
            if (note !== undefined) await timedFetch(root() + 'cart/update.js', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({note})});
            window.location.reload();
            return cart;
          }
          if (note !== undefined && fresh.querySelector('[data-note]')) fresh.querySelector('[data-note]').value = note;
          if (noteOpen && fresh.querySelector('details')) fresh.querySelector('details').open = true;
          lounge.replaceWith(fresh);
          fresh.querySelector('[data-live]').textContent = 'Cart updated. ' + cart.item_count + (cart.item_count === 1 ? ' item.' : ' items.');
          if (focusIndex !== null) {
            const focus = fresh.querySelector(`[data-line-index="${focusIndex}"] [data-quantity]`) || fresh.querySelector('[data-quantity]') || fresh.querySelector('h1');
            if (focus) { focus.setAttribute('tabindex', focus.matches('h1') ? '-1' : '0'); focus.focus({preventScroll: true}); }
          }
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && sections['cart-drawer']) {
          const freshDrawer = parse(sections['cart-drawer']).querySelector('#CartDrawer');
          const currentDrawer = drawer.querySelector('#CartDrawer');
          if (freshDrawer && currentDrawer) {
            currentDrawer.innerHTML = freshDrawer.innerHTML;
            drawer.classList.toggle('is-empty', cart.item_count === 0);
            drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
            if (drawerNote !== undefined && drawer.querySelector('#CartDrawer-Note')) drawer.querySelector('#CartDrawer-Note').value = drawerNote;
            if (openDrawer && cart.item_count > 0) drawer.open(trigger?.isConnected ? trigger : document.querySelector('#cart-icon-bubble'));
          }
        }
        if (sections['cart-icon-bubble']) {
          const header = document.getElementById('cart-icon-bubble');
          const content = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
          if (header && content) header.innerHTML = content.innerHTML;
        }
        syncRecommendations(cart);
        document.dispatchEvent(new CustomEvent('ss:cart-updated', {detail: {cart}}));
        return cart;
      } finally {
        disabled.forEach(([el, value]) => { if (el.isConnected) el.disabled = value; });
        this.busy = false;
      }
    }
  };
  window.ScaleShowroomCart = service;
  document.addEventListener('submit', async event => {
    const form = event.target.closest('form[data-ss-quick-add], form[data-ss-main-add]');
    if (!form) return;
    if (event.submitter?.closest('.shopify-payment-button, shopify-accelerated-checkout')) return;
    event.preventDefault();
    if (!form.reportValidity()) return;
    const status = form.querySelector('[data-ss-add-status], [data-status]');
    const button = form.querySelector('[type="submit"]');
    if (status) status.textContent = 'Adding to your cart…';
    try {
      await service.request('cart/add.js', new FormData(form), {openDrawer: !document.querySelector('[data-ss-cart-lounge]'), trigger: button});
      if (!form.isConnected) return;
      if (status) { status.replaceChildren(document.createTextNode('Added to your cart. ')); const link = document.createElement('a'); link.href = root() + 'cart'; link.textContent = 'View cart →'; status.append(link); }
      if (form.hasAttribute('data-ss-quick-add')) {
        const card = form.closest('[data-ss-product]');
        if (card) { card.hidden = true; const visible = [...card.parentElement.children].filter(el => !el.hidden); if (!visible.length) card.closest('.ss-display-addons')?.setAttribute('hidden',''); if (!document.querySelector('cart-drawer.active')) (visible[0]?.querySelector('button,a') || document.querySelector('#cart-icon-bubble'))?.focus({preventScroll: true}); }
      }
    } catch (error) {
      if (status) status.textContent = error.message || 'Could not add this item. Please try again.';
    }
  });
})();
